import { authClient } from '@app/auth/client';
import { Toaster } from '@app/ui';
import * as Sentry from '@sentry/react';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  ScrollRestoration,
} from '@tanstack/react-router';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { mountVercelToolbar } from '@vercel/toolbar/vite';
import posthog from 'posthog-js';
import { type ReactNode, useEffect, useRef } from 'react';
import globalsCss from '../globals.css?url';

// Initialize Sentry (client-side only)
if (typeof window !== 'undefined' && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Initialize PostHog (client-side only)
if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    // Auto-capture pageviews on initial load AND on SPA navigation
    // (PostHog hooks history.pushState/replaceState). The previous manual
    // PostHogPageview component fired on every TanStack pathname change,
    // which double-counts when we enable this — that component was
    // removed alongside this flag flip.
    capture_pageview: true,
    capture_pageleave: true,
    // Capture uncaught JS errors as $exception events. Surfaced zero
    // $exception events over 30d while sign-ins were measurably failing
    // (22/29 success rate per sessions; we had no visibility into WHY the
    // 7 failed). Sentry is the system-of-record for exceptions but
    // PostHog's $exception powers funnel-conditional analyses ("did
    // signin_started followed by $exception correlate with abandonment?").
    capture_exceptions: true,
  });
}

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'NilBoard - NIL Deal Management for College Athletes' },
      {
        name: 'description',
        content: 'Track, manage, and optimize your NIL deals. Built for college athletes.',
      },
      { property: 'og:title', content: 'NilBoard - NIL Deal Management for College Athletes' },
      {
        property: 'og:description',
        content: 'Track, manage, and optimize your NIL deals. Built for college athletes.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://www.nilboard.app' },
      { property: 'og:image', content: 'https://www.nilboard.app/og-image.svg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'NilBoard - NIL Deal Management' },
      {
        name: 'twitter:description',
        content: 'Track, manage, and optimize your NIL deals. Built for college athletes.',
      },
      { name: 'twitter:image', content: 'https://www.nilboard.app/og-image.svg' },
    ],
    links: [
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
      { rel: 'stylesheet', href: globalsCss },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      },
    ],
  }),
  errorComponent: ({ error: _error }) => (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-6">
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-4">
            We encountered an unexpected error. Please try refreshing the page.
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium shadow hover:bg-primary/90"
          >
            Go Home
          </a>
        </div>
        <Scripts />
      </body>
    </html>
  ),
  component: RootComponent,
});

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="font-sans antialiased">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground focus:underline focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Skip to main content
        </a>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function PostHogIdentify() {
  const { data: session, isPending } = authClient.useSession();
  const userId = session?.user?.id ?? null;
  const userEmail = session?.user?.email ?? null;
  const userName = session?.user?.name ?? null;
  const userRole =
    (session?.user as (typeof session.user & { role?: string | null }) | undefined)?.role ?? null;
  const identifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!import.meta.env.VITE_POSTHOG_KEY) return;
    // Skip while Better Auth is loading/refetching the session. Otherwise a
    // transient `data: null` mid-session (e.g. network blip during refetch)
    // would look like a sign-out and spuriously fire `signed_out` + reset(),
    // splitting the analytics identity. Only act once the session state is
    // settled.
    if (isPending) return;
    // identify() is idempotent — calling it with new traits updates the
    // person properties. The effect re-runs when ANY trait changes, so we
    // call identify unconditionally inside the userId branch (no
    // `!== userId` guard, which would skip trait updates for the same user).
    // The ref is only used to detect the authed -> unauthed transition.
    // No `signed_in` capture here: ref resets on every page load, which
    // would inflate the metric. Sign-in is captured at the auth callsites
    // (magic_link_requested, auth_method_selected).
    if (userId) {
      posthog.identify(userId, {
        email: userEmail,
        name: userName,
        role: userRole ?? 'user',
      });
      identifiedRef.current = userId;
    } else if (identifiedRef.current) {
      // Fire signed_out against the identified distinctId BEFORE reset()
      // clears it. This only runs on a real authed -> unauthed transition,
      // so a failed authClient.signOut() (which leaves the session intact)
      // never produces a spurious signed_out.
      posthog.capture('signed_out');
      posthog.reset();
      identifiedRef.current = null;
    }
  }, [userId, userEmail, userName, userRole, isPending]);

  return null;
}

function VercelToolbarMount() {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const unmount = mountVercelToolbar();
    return () => unmount();
  }, []);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <RootDocument>
      <Sentry.ErrorBoundary
        fallback={({ error }) => (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
              <p className="text-muted-foreground">
                {error instanceof Error ? error.message : 'Something went wrong'}
              </p>
            </div>
          </div>
        )}
      >
        <QueryClientProvider client={queryClient}>
          <PostHogIdentify />
          <main id="main-content">
            <Outlet />
          </main>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
      <Analytics />
      <SpeedInsights />
      <VercelToolbarMount />
      <Toaster />
    </RootDocument>
  );
}
